import React, { useState, useEffect } from 'react';
import { X, Shield, Settings } from 'lucide-react';
import { cookieManager, CookieSettings } from '../lib/cookies';

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = cookieManager.hasConsent();
    const currentSettings = cookieManager.getSettings();
    
    if (!hasConsent) {
      setShowBanner(true);
    }
    
    if (currentSettings) {
      setSettings(currentSettings);
    }
  }, []);

  const handleAcceptAll = () => {
    const newSettings: CookieSettings = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    cookieManager.setSettings(newSettings);
    cookieManager.setConsent(true);
    setSettings(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const newSettings: CookieSettings = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    cookieManager.setSettings(newSettings);
    cookieManager.setConsent(true);
    cookieManager.clearNonEssential();
    setSettings(newSettings);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSaveSettings = () => {
    cookieManager.setSettings(settings);
    cookieManager.setConsent(true);
    if (!settings.analytics || !settings.marketing || !settings.preferences) {
      cookieManager.clearNonEssential();
    }
    setShowBanner(false);
    setShowSettings(false);
  };

  const toggleSetting = (key: keyof CookieSettings) => {
    if (key === 'necessary') return; // Cannot toggle necessary cookies
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner && !showSettings) return null;

  if (showSettings) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-indigo-600 mr-2" />
              <h2 className="text-xl font-semibold">Cookie Settings</h2>
            </div>
            <button
              onClick={() => setShowSettings(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Necessary Cookies</h3>
                  <p className="text-sm text-gray-500">Required for the website to function properly</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.necessary}
                    disabled
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Analytics Cookies</h3>
                  <p className="text-sm text-gray-500">Help us improve our website</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.analytics}
                    onChange={() => toggleSetting('analytics')}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Marketing Cookies</h3>
                  <p className="text-sm text-gray-500">Used to deliver relevant advertisements</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.marketing}
                    onChange={() => toggleSetting('marketing')}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Preference Cookies</h3>
                  <p className="text-sm text-gray-500">Remember your preferences and settings</p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.preferences}
                    onChange={() => toggleSetting('preferences')}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Accept All
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 inset-x-0 bg-white border-t shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 pr-4">
            <p className="text-sm text-gray-700">
              We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <Settings className="h-4 w-4 mr-1" />
              Settings
            </button>
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}